import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { companyInfo } from '../data/mockData';
import { submitContact } from '../api/api';

const Contact = () => {
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    message: ''
  });

  const content = {
    en: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team for inquiries, quotes, or support',
      sendMessage: 'Send Message',
      name: 'Your Name',
      email: 'Email Address',
      company: 'Company Name',
      phone: 'Phone Number',
      country: 'Country',
      message: 'Your Message',
      office: 'Office Address',
      hours: 'Business Hours',
      hoursDetail: 'Monday - Friday: 9:00 AM - 6:00 PM IST',
      success: 'Message sent successfully! We will get back to you within 24 hours.',
      error: 'Please fill in all required fields.'
    },
    vi: {
      title: 'Liên hệ',
      subtitle: 'Liên hệ với nhóm của chúng tôi để hỏi thăm, báo giá hoặc hỗ trợ',
      sendMessage: 'Gửi tin nhắn',
      name: 'Tên của bạn',
      email: 'Địa chỉ email',
      company: 'Tên công ty',
      phone: 'Số điện thoại',
      country: 'Quốc gia',
      message: 'Tin nhắn của bạn',
      office: 'Địa chỉ văn phòng',
      hours: 'Giờ làm việc',
      hoursDetail: 'Thứ Hai - Thứ Sáu: 9:00 SA - 6:00 CH IST',
      success: 'Gửi tin nhắn thành công! Chúng tôi sẽ liên hệ lại trong vòng 24 giờ.',
      error: 'Vui lòng điền tất cả các trường bắt buộc.'
    }
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.company || !formData.phone || !formData.country || !formData.message) {
      toast.error(t.error);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitContact(formData);
      toast.success(t.success);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        country: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact submission error:', error);
      toast.error(language === 'en' ? 'Failed to send message. Please try again.' : 'Gửi tin nhắn thất bại. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h1>
            <p className="text-lg text-white/90">{t.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#264f78]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#264f78]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.office}</h3>
                    <p className="text-sm text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#264f78]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#264f78]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-sm text-[#264f78] hover:underline">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#264f78]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#264f78]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-sm text-[#264f78] hover:underline">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#264f78]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#264f78]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.hours}</h3>
                    <p className="text-sm text-gray-600">{t.hoursDetail}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">{t.name} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">{t.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company">{t.company} *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t.phone} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder=""
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">{t.country} *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder=""
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">{t.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      className="mt-1 min-h-[150px]"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#264f78] hover:bg-[#1a3a57] text-white py-6 text-lg transition-all duration-300"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        {language === 'en' ? 'Sending...' : 'Đang gửi...'}
                      </>
                    ) : (
                      <>
                        {t.sendMessage}
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
